import { AIModule } from '../types';

export const ROBOTICS_RL_MODULES: AIModule[] = [
  {
    id: 'robotics-rl-basics',
    title: 'Robotics RL Fundamentals',
    description: 'Apply reinforcement learning to robotic control and manipulation tasks.',
    estimatedTime: '90 minutes',
    duration: '90 minutes',
    difficulty: 'advanced' as const,
    content: `
# Robotics Reinforcement Learning

## Overview
Reinforcement learning in robotics enables robots to learn complex behaviors through trial and error, adapting to dynamic environments and improving performance over time.

## Key Concepts

### 1. Robot Control with RL
- **State representation**: Joint angles, velocities, sensor readings
- **Action spaces**: Continuous control signals, discrete commands
- **Reward design**: Task completion, efficiency, safety constraints

### 2. Sim-to-Real Transfer
- **Domain randomization**: Varying simulation parameters
- **Reality gap**: Bridging simulation and real-world differences
- **Progressive learning**: Gradual complexity increase

### 3. Multi-Modal Learning
- **Vision-based control**: Learning from camera inputs
- **Force feedback**: Tactile and proprioceptive learning
- **Sensor fusion**: Combining multiple sensory modalities

## Implementation

\`\`\`python
import gym
import numpy as np
from stable_baselines3 import PPO
import pybullet_envs

class RoboticArmEnv:
    def __init__(self):
        self.env = gym.make('KukaBulletEnv-v0')
        self.observation_space = self.env.observation_space
        self.action_space = self.env.action_space
        
    def reset(self):
        return self.env.reset()
        
    def step(self, action):
        obs, reward, done, info = self.env.step(action)
        
        # Custom reward shaping for manipulation tasks
        reward = self.compute_reward(obs, action, info)
        
        return obs, reward, done, info
        
    def compute_reward(self, obs, action, info):
        # Distance to target
        target_distance = np.linalg.norm(obs[:3] - info.get('target_pos', [0,0,0]))
        distance_reward = -target_distance
        
        # Energy efficiency
        energy_penalty = -0.01 * np.sum(np.square(action))
        
        # Task completion bonus
        completion_bonus = 100 if target_distance < 0.05 else 0
        
        return distance_reward + energy_penalty + completion_bonus

# Training setup
env = RoboticArmEnv()
model = PPO("MlpPolicy", env, verbose=1)
model.learn(total_timesteps=100000)
\`\`\`

## Applications
- **Manipulation**: Grasping, assembly, tool use
- **Navigation**: Path planning, obstacle avoidance
- **Human-robot interaction**: Collaborative tasks, safety

## Safety Considerations
- **Constraint satisfaction**: Hard safety limits
- **Risk-aware learning**: Conservative exploration
- **Human oversight**: Monitoring and intervention capabilities
`,
    learningObjectives: [
      'Apply RL algorithms to robotic control problems',
      'Design reward functions for robotic tasks',
      'Implement sim-to-real transfer techniques',
      'Handle continuous action spaces in robotics'
    ],
    quiz: {
      questions: [
        {
          id: 'robotics-rl-1',
          question: 'What is the main challenge in sim-to-real transfer for robotics RL?',
          options: [
            'Computational complexity',
            'Domain gap between simulation and reality',
            'Hardware limitations',
            'Algorithm selection'
          ],
          correctAnswer: 1,
          explanation: 'The domain gap between simulation and reality is the primary challenge, requiring techniques like domain randomization and careful transfer strategies.'
        },
        {
          id: 'robotics-rl-2',
          question: 'Which reward component is crucial for safe robotic learning?',
          options: [
            'Task completion bonus only',
            'Energy efficiency penalty',
            'Safety constraints and penalties',
            'Speed optimization'
          ],
          correctAnswer: 2,
          explanation: 'Safety constraints and penalties are crucial to prevent dangerous behaviors during learning.'
        },
        {
          id: 'robotics-rl-3',
          question: 'What type of action space is most common in robotic control?',
          options: [
            'Discrete actions',
            'Binary actions',
            'Continuous actions',
            'Categorical actions'
          ],
          correctAnswer: 2,
          explanation: 'Continuous action spaces are most common in robotics as they allow for smooth, precise control of actuators.'
        },
        {
          id: 'robotics-rl-4',
          question: 'How can sensor fusion improve RL in robotics?',
          options: [
            'Reduces computational requirements',
            'Provides richer state representations',
            'Eliminates need for simulation',
            'Simplifies reward design'
          ],
          correctAnswer: 1,
          explanation: 'Sensor fusion provides richer, more comprehensive state representations by combining multiple sensory inputs.'
        },
        {
          id: 'robotics-rl-5',
          question: 'What is domain randomization in robotics RL?',
          options: [
            'Random exploration strategies',
            'Varying simulation parameters during training',
            'Random robot configurations',
            'Stochastic policy design'
          ],
          correctAnswer: 1,
          explanation: 'Domain randomization involves varying simulation parameters during training to improve transfer to real-world scenarios.'
        }
      ]
    }
  }
];